import api from "config/axiosConfig";
import { apiRoutes } from "util/apiRoutes";

// Types for app settings
export interface AppSetting {
  id: number;
  key: string;
  properties: Record<string, any>;
  created_at: string;
  updated_at?: string;
}

export interface ReleaseNote {
  type: "feature" | "enhancement" | "fix" | "experimental";
  description: string;
}

export interface VersionNotes {
  version: string;
  date: string;
  notes: ReleaseNote[];
}

export interface ExperimentalFeature {
  /** Unique slug identifying the experimental feature (e.g. "graph-explorer") */
  feature_id: string;
  /** Feedback entity ID linked to this experimental feature */
  feedback_id: number;
  /** Human-readable name for the feature */
  title: string;
  /** Route prefix where the feature lives (e.g. "/graph") */
  route: string;
}

export interface AppSettingsResponse {
  total?: number;
  remaining?: number;
  settings: AppSetting[];
}

class AppSettingsService {
  /**
   * Get all app settings with filtering and pagination.
   */
  static async getAllAppSettings(params?: {
    keys?: string[];
    ids?: number[];
    limit?: number;
    offset?: number;
    sortField?: string;
    sortOrder?: string;
    count?: boolean;
  }): Promise<AppSetting[] | AppSettingsResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.keys && params.keys.length > 0) {
        queryParams.append("keys", params.keys.join(","));
      }
      
      if (params?.ids && params.ids.length > 0) {
        queryParams.append("ids", params.ids.join(","));
      }
      
      if (params?.limit) {
        queryParams.append("limit", params.limit.toString());
      }
      
      if (params?.offset) {
        queryParams.append("offset", params.offset.toString());
      }
      
      if (params?.sortField) {
        queryParams.append("sortField", params.sortField);
      }
      
      if (params?.sortOrder) {
        queryParams.append("sortOrder", params.sortOrder);
      }
      
      if (params?.count) {
        queryParams.append("count", "true");
      }

      const url = `${apiRoutes.appSettings.root}${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get a single app setting by ID.
   */
  static async getAppSettingById(id: number): Promise<AppSetting> {
    try {
      const response = await api.get(apiRoutes.appSettings.byId(id));
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new app setting.
   */
  static async createAppSetting(key: string, properties: Record<string, any>): Promise<AppSetting> {
    try {
      const response = await api.post(apiRoutes.appSettings.root, {
        key,
        properties,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update an existing app setting.
   */
  static async updateAppSetting(id: number, key: string, properties: Record<string, any>): Promise<AppSetting> {
    try {
      const response = await api.put(apiRoutes.appSettings.byId(id), {
        key,
        properties,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete an app setting.
   */
  static async deleteAppSetting(id: number): Promise<{ message: string }> {
    try {
      const response = await api.delete(apiRoutes.appSettings.byId(id));
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get release notes grouped by version (public endpoint).
   */
  static async getReleaseNotes(): Promise<VersionNotes[]> {
    try {
      const response = await api.get(apiRoutes.appSettings.releaseNotes);
      return response.data;
    } catch (error) {
      console.error("Error fetching release notes:", error);
      // Return empty array as fallback
      return [];
    }
  }

  /**
   * Get the current app version (public endpoint).
   */
  static async getCurrentVersion(): Promise<string> {
    try {
      const response = await api.get(apiRoutes.appSettings.version);
      return response.data.version;
    } catch (error) {
      console.error("Error fetching current version:", error);
      return "1.0.0";
    }
  }

  /**
   * Get all active experimental features (public endpoint).
   */
  static async getExperimentalFeatures(): Promise<ExperimentalFeature[]> {
    try {
      const response = await api.get(apiRoutes.appSettings.experimentalFeatures);
      return response.data;
    } catch (error) {
      console.error("Error fetching experimental features:", error);
      return [];
    }
  }

  /**
   * Get a single experimental feature by its feature_id slug (public endpoint).
   * Returns null if the feature is not found or not active.
   */
  static async getExperimentalFeature(featureId: string): Promise<ExperimentalFeature | null> {
    try {
      const features = await AppSettingsService.getExperimentalFeatures();
      return features.find((f) => f.feature_id === featureId) ?? null;
    } catch (error) {
      console.error("Error fetching experimental feature:", error);
      return null;
    }
  }
}

export default AppSettingsService; 